/**
 * Which painting to show, ported from apps/display/public/backgrounds.js.
 *
 * Balance stages, each with a painting for every time of day in landscape and
 * portrait -- five stages and two times today, all of it declared in
 * content/valley.toml (see content/README.md). The reference is the wall display, and the two have
 * to agree: a family looking at the kitchen screen and a phone at the same
 * moment must see the same valley, or the thing stops feeling like one place.
 *
 * ONE DELIBERATE GENERALISATION, and it is worth being explicit about.
 * backgrounds.js hardcodes the thresholds:
 *
 *     cents <= 0 ? 'barren' : cents < 3400 ? 'recovering'
 *   : cents < 6700 ? 'growing' : cents < 10000 ? 'thriving' : 'plentiful'
 *
 * Those numbers are 0%, 34%, 67% and 100% of 10000, and 10000 is exactly the
 * default `gardenTarget` in the data model -- which the reference then never
 * reads. A family that sets their target to $500 would watch the valley reach
 * full health at $100 and stay there, which is not what the setting says it
 * does. So the thresholds here are those same fractions OF THE FAMILY'S TARGET,
 * and scene.test.ts asserts that at the default target every boundary lands on
 * the reference's exact numbers.
 */

import { PAINTINGS, SCENE_PAINTINGS, type PaintingSet } from './art.generated';
import {
  DEFAULT_SCENE,
  DEFAULT_TARGET,
  SCENES,
  STAGE_LIST,
  STAGES,
  TIME_LIST,
  type Light,
  type Stage,
  type StageInfo,
  type TimeOfDay,
} from './content.generated';

// THE STAGES, THEIR THRESHOLDS AND THE TIMES OF DAY ARE DATA (2026-10-16):
// content/valley.toml, through content.generated.ts. Adding a stage or a
// time of day is an edit there and a rebuild, not an edit here. What stays
// here is the arithmetic, which is the part worth testing.
export { DEFAULT_TARGET, STAGES };
export type { Light, Stage, TimeOfDay };

const BY_ID = new Map(STAGE_LIST.map((info) => [info.id, info]));

export function stageInfo(name: Stage): StageInfo {
  return BY_ID.get(name)!;
}

// Thresholds as whole PERCENTAGES rather than fractions.
//
// Integers on purpose. Written the obvious way -- `cents < target * 0.34` --
// the boundary is wrong: 10000 * 0.34 is 3400.0000000000005 in IEEE754, so a
// balance of exactly $34.00 stays "recovering" while the wall display has
// already moved to "growing". Comparing `cents * 100 >= target * 34` keeps
// both sides whole and exact, which is the same reason every amount in this
// system is integer cents in the first place.
export function stage(cents: number, target: number = DEFAULT_TARGET): Stage {
  const full = target > 0 ? target : DEFAULT_TARGET;
  if (cents > 0) {
    for (let index = STAGE_LIST.length - 1; index > 0; index--) {
      const info = STAGE_LIST[index]!;
      if (info.fromPercent !== null && cents * 100 >= full * info.fromPercent) return info.id;
    }
  }
  return STAGE_LIST[0]!.id;
}

/** Which time of day an hour (0-23, local) falls in: the first that covers it. */
export function timeAt(hour: number): TimeOfDay {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  for (const time of TIME_LIST) {
    const covers = time.from < time.until ? h >= time.from && h < time.until : h >= time.from || h < time.until;
    if (covers) return time.id;
  }
  return TIME_LIST[0]!.id;
}

export function lightOf(time: TimeOfDay): Light {
  return TIME_LIST.find((entry) => entry.id === time)!.light;
}

/** The first time of day with a given light: what "night" means to old callers. */
function firstWithLight(light: Light): TimeOfDay {
  return TIME_LIST.find((entry) => entry.light === light)!.id;
}

/** The PaintingSet key for a time in an orientation: day, portraitDay. */
function slot(time: TimeOfDay, portrait: boolean): keyof PaintingSet {
  return (portrait ? `portrait${time[0]!.toUpperCase()}${time.slice(1)}` : time) as keyof PaintingSet;
}

// WHICH BACKGROUND (2026-10-18). A family chooses one in Settings; the
// server keeps the id with the family's settings and hands it to every
// device, a child's included. Like the family's unit (family/money.ts), it is
// set once when a family's state loads and read wherever the valley is drawn,
// rather than threaded through every component that paints one -- there is
// only ever one family on a screen. An id this build does not know (a scene
// removed, or added by a newer app) falls back to the default.
let familyScene: string = DEFAULT_SCENE;

export function setFamilyScene(id: string | null | undefined): void {
  familyScene = id && SCENE_PAINTINGS[id] ? id : DEFAULT_SCENE;
}

export function currentScene(): string {
  return familyScene;
}

export { SCENES, DEFAULT_SCENE };

/**
 * The file for a stage, looked up rather than worked out.
 *
 * Which file is in content/valley.toml, through art.generated.ts. Replacing
 * a painting is a one-line edit there and no edit here.
 */
export function paintingFor(name: Stage, time: TimeOfDay, portrait: boolean, scene: string = familyScene): string {
  const set = SCENE_PAINTINGS[scene] ?? PAINTINGS;
  return set[name][slot(time, portrait)];
}

/**
 * The time of day to paint, given the hour and the light a screen has
 * already decided on. They agree except when somebody forces the light (the
 * art preview's night switch); then the first time with that light wins.
 */
export function timeFor(hour: number, night: boolean): TimeOfDay {
  const time = timeAt(hour);
  return lightOf(time) === (night ? 'night' : 'day') ? time : firstWithLight(night ? 'night' : 'day');
}

/** The painting by light rather than by time, as the display has always asked. */
export function sceneName(name: Stage, night: boolean, portrait: boolean): string {
  return paintingFor(name, firstWithLight(night ? 'night' : 'day'), portrait);
}

// Every stage the thresholds above can return must have paintings. This line
// fails to compile if the manifest ever drops one, which is far better than a
// valley that goes blank the day a family's balance reaches it.
const _everyStagePainted: Record<Stage, PaintingSet> = PAINTINGS;
void _everyStagePainted;

/** Night, by the times in content/valley.toml (19:00 to 06:00 today). */
export function isNight(hour: number): boolean {
  return lightOf(timeAt(hour)) === 'night';
}

/**
 * Portrait, as the display decides it.
 *
 * Taller than it is wide AND narrow -- a phone held upright, not a desktop
 * window somebody has dragged into a thin shape, which is better served by the
 * landscape painting letterboxed.
 */
export function isPortrait(width: number, height: number): boolean {
  return width < 700 && height > width;
}

/** A new layout must keep its buildings/stations visible on tall tablets too. */
export function portraitForScene(width: number, height: number, scene = familyScene): boolean {
  return SCENES.find(s => s.id === scene)?.composition ? height > width : isPortrait(width, height);
}

export interface Scene {
  stage: Stage;
  /** Base name with no extension; the app serves .avif and .webp of each. */
  name: string;
}

export function sceneFor(
  cents: number,
  options: { hour: number; width: number; height: number; target?: number; night?: boolean },
): Scene {
  const at = stage(cents, options.target ?? DEFAULT_TARGET);
  const time = options.night === undefined ? timeAt(options.hour) : timeFor(options.hour, options.night);
  return {
    stage: at,
    name: paintingFor(at, time, portraitForScene(options.width, options.height)),
  };
}

/** How the stages read to a person, for the caption under the painting. */
export const STAGE_WORDS = Object.fromEntries(STAGE_LIST.map((info) => [info.id, info.words])) as Record<Stage, string>;

/** The short word for a stage, on Home: "dormant", "sprouting", "lush". */
export const STAGE_LABELS = Object.fromEntries(STAGE_LIST.map((info) => [info.id, info.label])) as Record<Stage, string>;
